import { useTheme } from '@mui/material';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Typography from '@mui/material/Typography';
import { SpaceStatus } from '@onesource/schemas';
import { useGetMyAnalytics, useGetSpace } from '@onesource/services';
import { Link } from '@tanstack/react-router';
import DOMPurify from 'dompurify';
import type { FC } from 'react';
import { DraftBadge } from 'src/components/draft-badge';

import { hasHTML } from '#/utils/has-html';

import { useContentRoute } from '../../hooks/use-content-route';
import { usePreviewMode } from '../../hooks/use-preview-mode';
import { CertificateSection } from './certificate-section';
import { MyAnalyticsPageSkeleton } from './my-analytics-page-skeleton';
import { RuleCard } from './rule-card';

/**
 * "My Analytics" (ANALYTICS sub-space): the member's own requirements from
 * every analytics rule that applies to them in this program, evaluated live.
 * Renders under the module slug route `/{program}/{sub-space}`; ids come from
 * the resolved content-route context.
 */
export const MyAnalyticsPage: FC = () => {
  const { programId, programSlug, subSpaceId = '' } = useContentRoute();
  const { isPreview, canPreview } = usePreviewMode();
  const theme = useTheme();
  const { data: parentSpace, isLoading: isLoadingParent, error: parentError } = useGetSpace(programId);
  const { data: space, isLoading: isLoadingSpace, error: spaceError } = useGetSpace(subSpaceId);
  const { data: analytics, isLoading: isLoadingAnalytics, error: analyticsError } = useGetMyAnalytics(subSpaceId);

  if (isLoadingParent || isLoadingSpace || isLoadingAnalytics) {
    return <MyAnalyticsPageSkeleton />;
  }

  if (parentError || spaceError) {
    return (
      <Box p={3}>
        <Alert severity="error">Failed to load: {parentError?.message || spaceError?.message}</Alert>
      </Box>
    );
  }

  if (!parentSpace || !space) {
    return (
      <Box p={3}>
        <Alert severity="error">Not found</Alert>
      </Box>
    );
  }

  if (!isPreview && (space.status === SpaceStatus.DRAFT || parentSpace.status === SpaceStatus.DRAFT)) {
    return (
      <Box p={3}>
        {canPreview ? (
          <Alert severity="info">
            This page is unpublished. Turn on “Preview unpublished content” above to view it.
          </Alert>
        ) : (
          <Alert severity="error">Not found</Alert>
        )}
      </Box>
    );
  }

  const sanitizedDescription = space.description ? DOMPurify.sanitize(space.description) : '';
  const rules = analytics?.rules ?? [];

  return (
    <Box sx={{ minHeight: 'calc(100vh - 180px)', overflow: { xs: 'visible', md: 'auto' } }}>
      <Box sx={{ pb: 2 }}>
        <Breadcrumbs
          sx={{
            mb: 2,
            fontFamily: '"Figtree", sans-serif',
            fontSize: 16,
            lineHeight: '22px',
            fontWeight: 400,
            color: theme.palette.grey[600],
            '& .MuiBreadcrumbs-separator': { mx: 0.6 },
          }}
        >
          <Link
            to="/programs/$programSlug"
            params={{ programSlug }}
            style={{ color: theme.palette.grey[600], textDecoration: 'none' }}
          >
            <Typography
              variant="body2"
              sx={{ mt: hasHTML(parentSpace.name) ? -0.5 : 0, fontSize: 16, color: theme.palette.grey[600] }}
            >
              <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parentSpace.name) }} />
            </Typography>
          </Link>
          {' / '}
          <Box component="span" sx={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 1 }}>
            <span dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(space.name) }} />
            {isPreview && space.status === SpaceStatus.DRAFT && <DraftBadge />}
          </Box>
        </Breadcrumbs>

        {sanitizedDescription && (
          <Box
            sx={{
              mb: 3,
              maxWidth: { xs: '100%', md: 'calc(100% - 315px)' },
              fontFamily: '"Figtree", sans-serif',
              fontSize: 16,
              lineHeight: '24px',
              color: '#2e2e2d',
              '& p': { m: 0 },
            }}
            dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
          />
        )}

        <Box
          sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: { xs: '100%', md: 'calc(100% - 205px)' } }}
        >
          {analyticsError && <Alert severity="error">Failed to load your requirements: {analyticsError.message}</Alert>}
          {!analyticsError && rules.length === 0 && (
            <Alert severity="info">No requirements are assigned to you yet.</Alert>
          )}
          {rules.map(rule => (
            <RuleCard key={rule.ruleId} rule={rule} programSlug={analytics?.programSlug ?? programSlug} />
          ))}
          {analytics && <CertificateSection status={analytics.certificate.status} />}
        </Box>
      </Box>
    </Box>
  );
};
